import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useLang } from "@/i18n";
import api, { extractErrorMessage } from "@/lib/api";
import PublicLayout from "@/components/layout/PublicLayout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { CalendarCheck, Loader2, XCircle } from "lucide-react";

function fmtLocal(iso, tz) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: tz,
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export default function BookingByToken() {
  const { token } = useParams();
  const { t } = useLang();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [reason, setReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const browserTz = (() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return null; }
  })();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/calendar/bookings/by-token/${token}`);
        if (!cancelled) setBooking(data);
      } catch (err) {
        if (!cancelled) {
          if (err?.response?.status === 404) setNotFound(true);
          else toast.error(extractErrorMessage(err, t("apiFallback.serverError")));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, t]);

  const handleCancel = async () => {
    if (!window.confirm(t("calendarMVP.view.cancelConfirm"))) return;
    setCancelling(true);
    try {
      const { data } = await api.post(
        `/calendar/bookings/by-token/${token}/cancel`,
        { cancelled_reason: reason || null },
      );
      setBooking(data);
      toast.success(t("calendarMVP.view.cancelSuccess"));
    } catch (err) {
      toast.error(extractErrorMessage(err, t("apiFallback.serverError")));
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-2xl px-4 py-16 text-center" data-testid="booking-by-token-loading">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-emerald-600" />
          <p className="mt-4 text-slate-600">{t("calendarMVP.view.loading")}</p>
        </div>
      </PublicLayout>
    );
  }
  if (notFound || !booking) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-2xl px-4 py-16 text-center" data-testid="booking-by-token-not-found">
          <XCircle className="mx-auto h-10 w-10 text-rose-500" />
          <h1 className="mt-4 text-2xl font-semibold text-slate-900">{t("calendarMVP.view.notFound")}</h1>
          <p className="mt-2 text-slate-600">{t("calendarMVP.view.notFoundBody")}</p>
        </div>
      </PublicLayout>
    );
  }

  const isCancelled = booking.status === "cancelled";
  const invTz = booking.invitee_tz || browserTz || booking.host_timezone;

  return (
    <PublicLayout>
      <div className="mx-auto max-w-2xl px-4 py-12" data-testid="booking-by-token-page">
        <Card className="overflow-hidden border-slate-200">
          <div className={`px-6 py-4 ${isCancelled ? "bg-rose-50 border-b border-rose-200" : "bg-emerald-50 border-b border-emerald-200"}`}>
            <div className="flex items-center gap-3">
              <CalendarCheck className={`h-6 w-6 ${isCancelled ? "text-rose-600" : "text-emerald-600"}`} />
              <div>
                <h1 className="text-xl font-semibold text-slate-900" data-testid="booking-title">
                  {t("calendarMVP.view.title")}
                </h1>
                <p className={`text-sm font-medium ${isCancelled ? "text-rose-700" : "text-emerald-700"}`} data-testid="booking-status">
                  {isCancelled ? t("calendarMVP.view.statusCancelled") : t("calendarMVP.view.statusConfirmed")}
                </p>
              </div>
            </div>
          </div>
          <div className="px-6 py-6 space-y-4">
            <Row label={booking.meeting_type_name_snapshot} value={t("calendarMVP.view.durationMin").replace("{n}", booking.meeting_type_duration_min_snapshot)} testId="booking-mt" />
            <Row label={t("calendarMVP.view.yourTime") + ` (${invTz})`} value={fmtLocal(booking.slot_start_utc, invTz)} testId="booking-invitee-time" />
            <Row label={t("calendarMVP.view.hostTime") + ` (${booking.host_timezone})`} value={fmtLocal(booking.slot_start_utc, booking.host_timezone)} testId="booking-host-time" />
            <Row label={t("calendarMVP.view.invitee")} value={`${booking.invitee_name} <${booking.invitee_email}>`} testId="booking-invitee" />
            {booking.notes && (
              <Row label={t("calendarMVP.view.notes")} value={booking.notes} testId="booking-notes" />
            )}
            {isCancelled && booking.cancelled_reason && (
              <Row label={t("calendarMVP.view.cancelReasonShown")} value={booking.cancelled_reason} testId="booking-cancel-reason" />
            )}
          </div>
          {!isCancelled && (
            <div className="border-t border-slate-200 px-6 py-5 bg-slate-50">
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t("calendarMVP.view.cancelReasonPlaceholder")}
                className="mb-3"
                maxLength={200}
                data-testid="booking-cancel-reason-input"
              />
              <Button
                onClick={handleCancel}
                disabled={cancelling}
                variant="destructive"
                data-testid="booking-cancel-btn"
              >
                {cancelling ? t("calendarMVP.view.cancelling") : t("calendarMVP.view.cancelCta")}
              </Button>
            </div>
          )}
        </Card>
      </div>
    </PublicLayout>
  );
}

function Row({ label, value, testId }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 border-b border-slate-100 pb-3" data-testid={testId}>
      <div className="text-sm uppercase tracking-wide text-slate-500 sm:w-44">{label}</div>
      <div className="text-slate-900 font-medium">{value}</div>
    </div>
  );
}
